/**
 * MEGA MENU V2 - ALOHA
 * Modular, no jQuery dependency
 * Handles: Desktop mega panels, Mobile drawer, Keyboard nav, Focus trap
 */

(function() {
  'use strict';

  // =========================================================================
  // Configuration
  // =========================================================================
  const CONFIG = {
    selectors: {
      header: '.mm-header',
      hamburger: '.mm-hamburger',
      drawer: '.mm-drawer',
      drawerClose: '.mm-drawer__close',
      overlay: '.mm-overlay',
      desktopNavItem: '.mm-nav-desktop__item',
      desktopNavLink: '.mm-nav-desktop__link',
      panel: '.mm-panel',
      mobileNavLink: '[data-mm-panel-trigger]',
      subpanel: '.mm-subpanel',
      subpanelBack: '.mm-subpanel__back',
      accordion: '.mm-accordion',
      accordionTrigger: '.mm-accordion__trigger',
    },
    classes: {
      drawerOpen: 'mm-drawer--open',
      panelOpen: 'mm-panel--open',
      subpanelOpen: 'mm-subpanel--open',
      overlayVisible: 'mm-overlay--visible',
      navItemOpen: 'mm-nav-desktop__item--open',
      accordionOpen: 'mm-accordion--open',
      bodyLock: 'mm-drawer-open',
    },
    keys: {
      ESCAPE: 'Escape',
      ENTER: 'Enter',
      SPACE: ' ',
      TAB: 'Tab',
      ARROW_DOWN: 'ArrowDown',
      ARROW_UP: 'ArrowUp',
      ARROW_LEFT: 'ArrowLeft',
      ARROW_RIGHT: 'ArrowRight',
    },
    transitionDuration: 200,
  };

  // =========================================================================
  // Utility Functions
  // =========================================================================

  /**
   * Get all focusable elements within a container
   */
  function getFocusableElements(container) {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    return Array.from(container.querySelectorAll(focusableSelectors));
  }

  /**
   * Trap focus within a container
   */
  function trapFocus(container, event) {
    const focusableElements = getFocusableElements(container);
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  /**
   * Debounce function
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // =========================================================================
  // Desktop Mega Menu
  // =========================================================================
  class DesktopMegaMenu {
    constructor() {
      this.navItems = document.querySelectorAll(CONFIG.selectors.desktopNavItem);
      this.activeItem = null;
      this.hoverTimeout = null;
      this.init();
    }

    init() {
      if (!this.navItems.length) return;

      this.navItems.forEach(item => {
        const link = item.querySelector(CONFIG.selectors.desktopNavLink);
        const panel = item.querySelector(CONFIG.selectors.panel);

        if (!panel) return;

        // Mouse events
        item.addEventListener('mouseenter', () => this.openPanel(item));
        item.addEventListener('mouseleave', () => this.scheduleClose(item));

        // Keyboard events
        link?.addEventListener('keydown', (e) => this.handleKeydown(e, item));
        link?.addEventListener('focus', () => this.openPanel(item));

        // Panel keyboard events
        panel.addEventListener('keydown', (e) => this.handlePanelKeydown(e, item));
      });

      // Close on click outside
      document.addEventListener('click', (e) => this.handleOutsideClick(e));

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === CONFIG.keys.ESCAPE && this.activeItem) {
          this.closePanel(this.activeItem);
          this.activeItem.querySelector(CONFIG.selectors.desktopNavLink)?.focus();
        }
      });
    }

    openPanel(item) {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }

      // Close other panels
      this.navItems.forEach(navItem => {
        if (navItem !== item) {
          this.closePanel(navItem);
        }
      });

      const panel = item.querySelector(CONFIG.selectors.panel);
      const link = item.querySelector(CONFIG.selectors.desktopNavLink);

      if (panel) {
        item.classList.add(CONFIG.classes.navItemOpen);
        panel.classList.add(CONFIG.classes.panelOpen);
        link?.setAttribute('aria-expanded', 'true');
        this.activeItem = item;
      }
    }

    closePanel(item) {
      const panel = item.querySelector(CONFIG.selectors.panel);
      const link = item.querySelector(CONFIG.selectors.desktopNavLink);

      if (panel) {
        item.classList.remove(CONFIG.classes.navItemOpen);
        panel.classList.remove(CONFIG.classes.panelOpen);
        link?.setAttribute('aria-expanded', 'false');

        if (this.activeItem === item) {
          this.activeItem = null;
        }
      }
    }

    scheduleClose(item) {
      this.hoverTimeout = setTimeout(() => {
        this.closePanel(item);
      }, 100);
    }

    handleKeydown(e, item) {
      const panel = item.querySelector(CONFIG.selectors.panel);

      switch (e.key) {
        case CONFIG.keys.ENTER:
        case CONFIG.keys.SPACE:
          if (panel) {
            e.preventDefault();
            this.openPanel(item);
            const firstFocusable = getFocusableElements(panel)[0];
            firstFocusable?.focus();
          }
          break;
        case CONFIG.keys.ARROW_DOWN:
          if (panel) {
            e.preventDefault();
            this.openPanel(item);
            const firstFocusable = getFocusableElements(panel)[0];
            firstFocusable?.focus();
          }
          break;
        case CONFIG.keys.ESCAPE:
          this.closePanel(item);
          break;
      }
    }

    handlePanelKeydown(e, item) {
      if (e.key === CONFIG.keys.ESCAPE) {
        e.preventDefault();
        this.closePanel(item);
        item.querySelector(CONFIG.selectors.desktopNavLink)?.focus();
      }
    }

    handleOutsideClick(e) {
      if (this.activeItem && !this.activeItem.contains(e.target)) {
        this.closePanel(this.activeItem);
      }
    }
  }

  // =========================================================================
  // Mobile Drawer
  // =========================================================================
  class MobileDrawer {
    constructor() {
      this.drawer = document.querySelector(CONFIG.selectors.drawer);
      this.hamburger = document.querySelector(CONFIG.selectors.hamburger);
      this.closeBtn = document.querySelector(CONFIG.selectors.drawerClose);
      this.overlay = document.querySelector(CONFIG.selectors.overlay);
      this.isOpen = false;
      this.previousFocus = null;
      this.init();
    }

    init() {
      if (!this.drawer || !this.hamburger) return;

      // Toggle drawer (open if closed, close if open)
      this.hamburger.addEventListener('click', () => this.toggle());

      // Close drawer
      this.closeBtn?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());

      // Keyboard events
      document.addEventListener('keydown', (e) => {
        if (e.key === CONFIG.keys.ESCAPE && this.isOpen) {
          this.close();
        }
        if (e.key === CONFIG.keys.TAB && this.isOpen) {
          trapFocus(this.drawer, e);
        }
      });

      // Handle resize
      window.addEventListener('resize', debounce(() => {
        if (window.innerWidth >= 992 && this.isOpen) {
          this.close();
        }
      }, 100));

      // Close drawer when search opens (mutual exclusivity)
      document.addEventListener('mm:search:open', () => {
        if (this.isOpen) {
          this.close();
        }
      });
    }

    open() {
      this.previousFocus = document.activeElement;
      this.isOpen = true;

      // IMPORTANT: Reset all subpanels to parent menu state on every open
      // This ensures clean state even if drawer was closed while in a subpanel
      const allSubpanels = this.drawer.querySelectorAll(CONFIG.selectors.subpanel);
      allSubpanels.forEach(panel => {
        panel.classList.add('mm-subpanel--instant');
        panel.classList.remove(CONFIG.classes.subpanelOpen);
      });

      // Reset aria-expanded on all triggers
      const allTriggers = this.drawer.querySelectorAll(CONFIG.selectors.mobileNavLink);
      allTriggers.forEach(trigger => {
        trigger.setAttribute('aria-expanded', 'false');
      });

      // Remove instant class after a frame
      requestAnimationFrame(() => {
        allSubpanels.forEach(panel => {
          panel.classList.remove('mm-subpanel--instant');
        });
      });

      document.body.classList.add(CONFIG.classes.bodyLock);
      this.drawer.classList.add(CONFIG.classes.drawerOpen);
      this.overlay?.classList.add(CONFIG.classes.overlayVisible);
      this.hamburger.setAttribute('aria-expanded', 'true');

      // Focus first focusable element
      setTimeout(() => {
        const firstFocusable = getFocusableElements(this.drawer)[0];
        firstFocusable?.focus();
      }, CONFIG.transitionDuration);

      // Dispatch event
      document.dispatchEvent(new CustomEvent('mm:drawer:open'));
    }

    close() {
      this.isOpen = false;

      document.body.classList.remove(CONFIG.classes.bodyLock);
      this.drawer.classList.remove(CONFIG.classes.drawerOpen);
      this.overlay?.classList.remove(CONFIG.classes.overlayVisible);
      this.hamburger.setAttribute('aria-expanded', 'false');

      // Close any open subpanels instantly (no transition needed when drawer closes)
      const allSubpanels = this.drawer.querySelectorAll(CONFIG.selectors.subpanel);
      allSubpanels.forEach(panel => {
        panel.classList.add('mm-subpanel--instant');
        panel.classList.remove(CONFIG.classes.subpanelOpen);
      });

      // Reset aria-expanded on all triggers
      const allTriggers = this.drawer.querySelectorAll(CONFIG.selectors.mobileNavLink);
      allTriggers.forEach(trigger => {
        trigger.setAttribute('aria-expanded', 'false');
      });

      // Remove instant class after drawer transition completes
      setTimeout(() => {
        allSubpanels.forEach(panel => {
          panel.classList.remove('mm-subpanel--instant');
        });
      }, CONFIG.transitionDuration);

      // Restore focus
      this.previousFocus?.focus();

      // Dispatch event
      document.dispatchEvent(new CustomEvent('mm:drawer:close'));
    }

    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }
  }

  // =========================================================================
  // Mobile Sub-panels (Drill-down)
  // =========================================================================
  class MobileSubpanels {
    constructor() {
      this.triggers = document.querySelectorAll(CONFIG.selectors.mobileNavLink);
      this.backButtons = document.querySelectorAll(CONFIG.selectors.subpanelBack);
      this.drawerBody = document.querySelector('.mm-drawer__body');
      this.isTransitioning = false;
      this.currentPanel = null;
      this.init();
    }

    init() {
      if (!this.triggers.length) return;

      // Open subpanel
      this.triggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
          // Prevent rapid clicking
          if (this.isTransitioning) return;

          const panelId = trigger.getAttribute('data-mm-panel-trigger');
          const panel = document.getElementById(panelId);

          if (panel) {
            e.preventDefault();
            this.openSubpanel(panel, trigger);
          }
        });

        trigger.addEventListener('keydown', (e) => {
          if (e.key === CONFIG.keys.ENTER || e.key === CONFIG.keys.SPACE) {
            // Prevent rapid clicking
            if (this.isTransitioning) return;

            const panelId = trigger.getAttribute('data-mm-panel-trigger');
            const panel = document.getElementById(panelId);

            if (panel) {
              e.preventDefault();
              this.openSubpanel(panel, trigger);
            }
          }
        });
      });

      // Back buttons
      this.backButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          // Prevent rapid clicking
          if (this.isTransitioning) return;

          e.preventDefault();
          const panel = btn.closest(CONFIG.selectors.subpanel);
          if (panel) {
            this.closeSubpanel(panel);
          }
        });

        btn.addEventListener('keydown', (e) => {
          if (e.key === CONFIG.keys.ENTER || e.key === CONFIG.keys.SPACE) {
            // Prevent rapid clicking
            if (this.isTransitioning) return;

            e.preventDefault();
            const panel = btn.closest(CONFIG.selectors.subpanel);
            if (panel) {
              this.closeSubpanel(panel);
            }
          }
        });
      });
    }

    openSubpanel(panel, trigger) {
      // Set transitioning lock
      this.isTransitioning = true;

      // Add pointer-events protection during transition
      this.drawerBody?.classList.add('mm-transitioning');

      // Force close all other subpanels instantly (no transition)
      const allSubpanels = document.querySelectorAll(CONFIG.selectors.subpanel);
      allSubpanels.forEach(otherPanel => {
        if (otherPanel !== panel) {
          // Add instant-close class to disable transition
          otherPanel.classList.add('mm-subpanel--instant');
          otherPanel.classList.remove(CONFIG.classes.subpanelOpen);
          const otherPanelTriggerId = otherPanel.id;
          const otherPanelTrigger = document.querySelector(`[data-mm-panel-trigger="${otherPanelTriggerId}"]`);
          otherPanelTrigger?.setAttribute('aria-expanded', 'false');

          // Remove instant class after a frame
          requestAnimationFrame(() => {
            otherPanel.classList.remove('mm-subpanel--instant');
          });
        }
      });

      // Open the new panel
      panel.classList.add(CONFIG.classes.subpanelOpen);
      trigger.setAttribute('aria-expanded', 'true');
      this.currentPanel = panel;

      // Release lock and focus back button after transition
      setTimeout(() => {
        this.isTransitioning = false;
        this.drawerBody?.classList.remove('mm-transitioning');
        const backBtn = panel.querySelector(CONFIG.selectors.subpanelBack);
        backBtn?.focus();
      }, CONFIG.transitionDuration);

      // Dispatch event
      document.dispatchEvent(new CustomEvent('mm:subpanel:open', { detail: { panel } }));
    }

    closeSubpanel(panel) {
      // Set transitioning lock
      this.isTransitioning = true;

      // Add pointer-events protection during transition
      this.drawerBody?.classList.add('mm-transitioning');

      const triggerId = panel.id;
      const trigger = document.querySelector(`[data-mm-panel-trigger="${triggerId}"]`);

      panel.classList.remove(CONFIG.classes.subpanelOpen);
      trigger?.setAttribute('aria-expanded', 'false');
      this.currentPanel = null;

      // Release lock and restore focus after transition
      setTimeout(() => {
        this.isTransitioning = false;
        this.drawerBody?.classList.remove('mm-transitioning');
        trigger?.focus();
      }, CONFIG.transitionDuration);

      // Dispatch event
      document.dispatchEvent(new CustomEvent('mm:subpanel:close', { detail: { panel } }));
    }
  }

  // =========================================================================
  // Accordion (Account section)
  // =========================================================================
  class Accordion {
    constructor() {
      this.accordions = document.querySelectorAll(CONFIG.selectors.accordion);
      this.init();
    }

    init() {
      if (!this.accordions.length) return;

      this.accordions.forEach(accordion => {
        const trigger = accordion.querySelector(CONFIG.selectors.accordionTrigger);

        trigger?.addEventListener('click', () => this.toggle(accordion));

        trigger?.addEventListener('keydown', (e) => {
          if (e.key === CONFIG.keys.ENTER || e.key === CONFIG.keys.SPACE) {
            e.preventDefault();
            this.toggle(accordion);
          }
        });
      });
    }

    toggle(accordion) {
      const isOpen = accordion.classList.contains(CONFIG.classes.accordionOpen);
      const trigger = accordion.querySelector(CONFIG.selectors.accordionTrigger);

      if (isOpen) {
        accordion.classList.remove(CONFIG.classes.accordionOpen);
        trigger?.setAttribute('aria-expanded', 'false');
      } else {
        accordion.classList.add(CONFIG.classes.accordionOpen);
        trigger?.setAttribute('aria-expanded', 'true');
      }
    }
  }

  // =========================================================================
  // Header Scroll Behavior (optional - hide on scroll down)
  // =========================================================================
  class HeaderScroll {
    constructor() {
      this.header = document.querySelector(CONFIG.selectors.header);
      this.lastScrollY = 0;
      this.ticking = false;
      this.init();
    }

    init() {
      if (!this.header) return;

      window.addEventListener('scroll', () => {
        if (!this.ticking) {
          window.requestAnimationFrame(() => {
            this.handleScroll();
            this.ticking = false;
          });
          this.ticking = true;
        }
      });
    }

    handleScroll() {
      const currentScrollY = window.scrollY;

      // Only hide after scrolling past header height
      if (currentScrollY < 80) {
        this.header.classList.remove('mm-header--hidden');
        this.lastScrollY = currentScrollY;
        return;
      }

      if (currentScrollY > this.lastScrollY) {
        // Scrolling down - hide header
        this.header.classList.add('mm-header--hidden');
      } else {
        // Scrolling up - show header
        this.header.classList.remove('mm-header--hidden');
      }

      this.lastScrollY = currentScrollY;
    }
  }

  // =========================================================================
  // Initialize
  // =========================================================================
  function init() {
    // Wait for DOM
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initComponents);
    } else {
      initComponents();
    }
  }

  function initComponents() {
    new DesktopMegaMenu();
    new MobileDrawer();
    new MobileSubpanels();
    new Accordion();
    // Uncomment to enable hide-on-scroll:
    // new HeaderScroll();
  }

  // Run initialization
  init();

  // Expose to global scope for Shopify section refresh
  window.MegaMenuV2 = {
    init: initComponents,
    DesktopMegaMenu,
    MobileDrawer,
    MobileSubpanels,
    Accordion,
    HeaderScroll,
  };

})();
